import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Terminal, LogOut, Sun, Moon, Menu, X } from "lucide-react";

const linkCls = ({ isActive }) =>
  `text-xs font-mono uppercase tracking-wider px-3 py-2 transition-colors ${
    isActive ? "text-zinc-900 dark:text-white" : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
  }`;

const mobileLinkCls = ({ isActive }) =>
  `block w-full text-left text-sm font-mono uppercase tracking-wider px-4 py-3 border-l-2 transition-colors ${
    isActive
      ? "border-[#FF3B30] text-zinc-900 dark:text-white bg-zinc-100 dark:bg-white/5"
      : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-white/5"
  }`;

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-button"
      aria-label="Toggle theme"
      title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
      className="w-9 h-9 flex items-center justify-center border border-zinc-300 dark:border-white/20 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
    >
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function UserLinks({ user, cls, prefix = "nav" }) {
  return (
    <>
      <NavLink to="/dashboard" data-testid={`${prefix}-dashboard`} className={cls}>Dashboard</NavLink>
      <NavLink to="/labs" data-testid={`${prefix}-labs`} className={cls}>Labs</NavLink>
      <NavLink to="/subscription" data-testid={`${prefix}-subscription`} className={cls}>Subscription</NavLink>
      {user.role === "admin" && (
        <NavLink to="/admin" data-testid={`${prefix}-admin`} className={cls}>Admin</NavLink>
      )}
      <NavLink to="/profile" data-testid={`${prefix}-profile`} className={cls}>Profile</NavLink>
    </>
  );
}

function GuestLinks({ cls }) {
  const c = cls({ isActive: false });
  return (
    <>
      <a href="/#curriculum" className={c}>Curriculum</a>
      <a href="/#why" className={c}>Why ILFAL</a>
      <a href="/#pricing" className={c}>Pricing</a>
    </>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname, hash]);

  const doLogout = async () => { setOpen(false); await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-black/70 border-b border-zinc-200 dark:border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center group-hover:bg-zinc-900 dark:group-hover:bg-white transition-colors">
            <Terminal className="w-5 h-5 text-white group-hover:text-white dark:group-hover:text-black transition-colors" />
          </div>
          <span className="font-display font-black text-lg tracking-tighter text-zinc-900 dark:text-white uppercase">ILFAL</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
          {user ? <UserLinks user={user} cls={linkCls} /> : <GuestLinks cls={linkCls} />}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user === undefined ? null : user ? (
            <>
              <span className="hidden lg:inline text-xs font-mono text-zinc-500 mr-1" data-testid="nav-user-email">{user.email}</span>
              <Button
                data-testid="nav-logout-button"
                size="sm"
                onClick={doLogout}
                className="hidden md:inline-flex rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black"
              >
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-button" size="sm" onClick={() => nav("/login")}
                className="hidden sm:inline-flex rounded-none bg-transparent text-zinc-600 dark:text-zinc-300 font-mono text-xs uppercase tracking-wider hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-white/10 dark:hover:text-white">
                Log in
              </Button>
              <Button data-testid="nav-register-button" size="sm" onClick={() => nav("/register")}
                className="rounded-none bg-[#FF3B30] text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black">
                Get started
              </Button>
            </>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="md:hidden w-9 h-9 flex items-center justify-center border border-zinc-300 dark:border-white/20 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0A0A0A]" data-testid="nav-mobile-menu">
          <nav className="max-w-7xl mx-auto px-2 py-2 flex flex-col">
            {user ? <UserLinks user={user} cls={mobileLinkCls} prefix="nav-mobile" /> : <GuestLinks cls={mobileLinkCls} />}
          </nav>
          <div className="border-t border-zinc-200 dark:border-white/10 px-4 py-3 flex items-center justify-between gap-3">
            {user ? (
              <>
                <span className="text-xs font-mono text-zinc-500 truncate" data-testid="nav-mobile-user-email">{user.email}</span>
                <Button
                  data-testid="nav-mobile-logout-button"
                  size="sm"
                  onClick={doLogout}
                  className="shrink-0 rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black"
                >
                  <LogOut className="w-4 h-4 mr-1" /> Logout
                </Button>
              </>
            ) : (
              <Button data-testid="nav-mobile-login-button" size="sm" onClick={() => nav("/login")}
                className="w-full rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black">
                Log in
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
